import type { AssistantTask } from '@crowdex/agent-contracts/api';
import { Badge } from '@crowdex/ui/components/badge';
import { cn } from '@crowdex/ui/lib/utils';
import { FileText } from 'lucide-react';
import {
  automationName,
  costLabel,
  type PlanRow,
  plannedState,
  taskIconState,
  taskStepLine,
  tasksSummary,
} from './assistant-model';
import { TaskStateIcon } from './assistant-parts';

/**
 * The project panel's Tasks (D65): each task (a run of an automation, or an agent's work alone), a
 * child conversation of the conversation that asked for it — its state, its automation and step, its branch and cost — with
 * its agent steps nested. Another member's task shows who asked; it opens for them only.
 */
export function AssistantTasksTab({
  tasks,
  automations = [],
  onOpen,
}: {
  tasks: readonly AssistantTask[] | null;
  /** The user's automations, which name a task from before names were kept. */
  automations?: readonly { id: string; name: string }[];
  onOpen: (conversationId: string) => void;
}) {
  if (tasks === null) return <p className="type-meta p-4 text-muted-foreground">Loading…</p>;
  return (
    <div className="flex flex-col gap-3 px-4 py-3" data-project-tasks>
      <p className="type-meta text-muted-foreground">
        {tasks.length === 0
          ? 'No task yet: ask the assistant’s conversation for work, its agent starts one.'
          : tasksSummary(tasks)}
      </p>
      <ul className="flex flex-col gap-1">
        {tasks.map((task) => (
          <li key={task.conversationId}>
            <TaskRow
              onOpen={onOpen}
              planned={plannedState(task.conversationId, tasks)}
              automations={automations}
              task={task}
            />
            {task.mine && task.children.length > 0 && (
              <ul className="ml-7 flex flex-col">
                {task.children.map((child) => (
                  <li key={child.conversationId}>
                    <button
                      className="flex w-full items-start gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-accent/50"
                      onClick={() => onOpen(child.conversationId)}
                      type="button"
                    >
                      <FileText
                        aria-hidden
                        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-sm">
                          {child.title} · {child.agentName}
                        </span>
                        <span className="type-meta block truncate text-muted-foreground">
                          step of the task above
                        </span>
                        {task.pullRequest !== null && (
                          <span className="block truncate font-mono text-subtle-foreground text-xs">
                            on PR #{task.pullRequest.number}
                          </span>
                        )}
                      </span>
                      <span className="type-meta shrink-0 text-muted-foreground">
                        {costLabel(child.costUsd)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {tasks.length > 0 && (
        <p className="type-meta text-muted-foreground">
          Each one is a full conversation with its own branch and PR; a review step is a child of
          its task. It stays under this one in the list; open it to talk to it.
        </p>
      )}
    </div>
  );
}

function TaskRow({
  task,
  planned,
  automations,
  onOpen,
}: {
  task: AssistantTask;
  /** Where its plan holds it, when a run plans it. */
  planned: PlanRow['state'] | null;
  automations: readonly { id: string; name: string }[];
  onOpen: (conversationId: string) => void;
}) {
  const state = taskIconState(task);
  const requester = task.workflowRun.request?.requester;
  const workflow = automationName(task.workflowRun, automations);
  return (
    <button
      className="flex w-full items-start gap-2.5 rounded-xl px-2 py-2 text-left enabled:hover:bg-accent/50"
      data-task-row={task.conversationId}
      disabled={!task.mine}
      onClick={() => onOpen(task.conversationId)}
      title={task.mine ? undefined : 'Another member’s task: it opens for them.'}
      type="button"
    >
      <TaskStateIcon className="mt-0.5" state={state} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-sm">{task.title}</span>
        <span className="mt-0.5 flex min-w-0 items-center gap-1.5">
          {workflow !== null && <Badge size="sm">{workflow}</Badge>}
          <span
            className={cn(
              'type-meta truncate',
              state === 'waiting' ? 'text-attention' : 'text-muted-foreground',
            )}
          >
            {taskStepLine(task, planned)}
            {!task.mine && requester ? ` · ${requester}` : ''}
          </span>
        </span>
        {task.branch !== null && (
          <span className="mt-0.5 block truncate font-mono text-subtle-foreground text-xs">
            {task.branch}
          </span>
        )}
      </span>
      <span className="type-meta shrink-0 text-muted-foreground">
        {costLabel(task.workflowRun.costUsd)}
      </span>
    </button>
  );
}
